import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { usePropertyField, useProperty, useContentToken, EditableToken } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap,
  faUtensils,
  faMugHot,
  faDumbbell,
  faBowlingBall,
  faLocationDot,
  faCartShopping,
  faTree,
  faTrain,
  faHeartPulse,
  faFilm,
  faStore,
  faPlus,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
  poi_map_icon?: string;
  poi_google_place_id?: string;
  poi_place_primary_type?: string;
}

// Filter out the empty/placeholder string values we sometimes get back from
// the content pipeline so popup actions only render when they have real data.
function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

// Popup addresses look tidier without the country tail — the map is already
// implicitly US-only for these properties, so ", USA" (or " USA") at the end
// is just noise.
function stripCountry(address: string): string {
  return address.replace(/,?\s*USA\.?\s*$/i, '').trim();
}

// Split a US-style address into street (line 1) and city/state/zip (line 2).
// Matches "303 Cooper St, Camden, NJ 08102" → street + "Camden, NJ 08102".
// Falls back to a single line when the pattern isn't recognizable.
function splitAddressLines(address: string): { street: string; locality: string | null } {
  const clean = stripCountry(address);
  const match = clean.match(
    /^(.+),\s*([^,]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)$/i,
  );
  if (match) {
    return { street: match[1].trim(), locality: match[2].trim() };
  }
  return { street: clean, locality: null };
}

function appendAddressBlock(parent: HTMLElement, address: string, styleExtra = '') {
  const { street, locality } = splitAddressLines(address);
  if (!street) return;

  const addr = document.createElement('div');
  addr.style.cssText =
    `font-size:12px;color:#666;margin-top:3px;line-height:1.4;text-align:center;${styleExtra}`;

  const streetEl = document.createElement('div');
  streetEl.textContent = street;
  addr.appendChild(streetEl);

  if (locality) {
    const localityEl = document.createElement('div');
    localityEl.textContent = locality;
    addr.appendChild(localityEl);
  }

  parent.appendChild(addr);
}

// ============================================================================
// Category icons
// ============================================================================

function getCategoryIcon(category: string) {
  const lower = (category || '').toLowerCase();
  if (/school|colleg|educ|univers|academ/.test(lower)) return faGraduationCap;
  if (/coffee|cafe|espresso|bakery/.test(lower)) return faMugHot;
  if (/restaurant|dining|food|eatery|bistro/.test(lower)) return faUtensils;
  if (/gym|fitness|yoga|crossfit|pilates/.test(lower)) return faDumbbell;
  if (/activit|bowling|entertain|leisure|recreation/.test(lower)) return faBowlingBall;
  if (/shop|retail|store|mall|boutique/.test(lower)) return faCartShopping;
  if (/park|nature|trail|garden|outdoor/.test(lower)) return faTree;
  if (/transit|train|station|bus|subway/.test(lower)) return faTrain;
  if (/health|medical|hospital|clinic|pharmac/.test(lower)) return faHeartPulse;
  if (/theater|theatre|cinema|movie|film/.test(lower)) return faFilm;
  if (/grocer|supermarket|market/.test(lower)) return faStore;
  return faLocationDot;
}

// ============================================================================
// Map styling & markers
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) { reject(new Error('No key')); return; }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => { delete (window as any)[cb]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => { mapsLoaderPromise = null; reject(new Error('Script failed')); };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 30): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.3)}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makeHighlightMarker(fillColor: string, glyphSvg: string, size = 40): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.35)}" viewBox="0 0 40 54">
  <ellipse cx="20" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M20 0C9 0 0 9 0 20c0 15 20 31 20 31s20-16 20-31C40 9 31 0 20 0z" fill="${fillColor}" stroke="#fff" stroke-width="2"/>
  <g transform="translate(10,10)" fill="#fff"><g transform="translate(10,10) scale(0.7) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

// Aion override: the property pin is a solid surface-theme orange (#e2903d)
// with no logo or house glyph inside — just the pin shape — so it reads as a
// clean location marker in the brand accent used elsewhere on the site
// (POI hover, CTAs, etc.) regardless of what primary color a given property has.
const AION_PROPERTY_PIN_COLOR = '#e2903d';

function makePropertyPinBg(): string {
  const pinColor = AION_PROPERTY_PIN_COLOR;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${pinColor}" stroke="#fff" stroke-width="2.5"/>
</svg>`)}`;
}

const CATEGORY_GLYPHS: Record<string, string> = {
  restaurant: '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>',
  shopping: '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>',
  education: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>',
  fitness: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>',
  health: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>',
  entertainment: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>',
  park: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>',
  transit: '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>',
  grocery: '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>',
  default: '<circle cx="8" cy="8" r="4" fill="#fff"/>',
};

function getCategoryGlyph(poiType: string): string {
  const lower = (poiType || '').toLowerCase();
  for (const [key, glyph] of Object.entries(CATEGORY_GLYPHS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return glyph;
  }
  return CATEGORY_GLYPHS.default;
}

function isColorDark(hex: string): boolean {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return false;
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  // Perceived luminance (Rec. 709)
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 0.55;
}

// Return black or white — whichever gives readable contrast on the given
// background. Applied consistently to any colored surface (primary panel,
// secondary hover/active state) so text never washes into its background.
function contrastText(hex: string): string {
  return isColorDark(hex) ? '#ffffff' : '#000000';
}

// A subtle, contrast-aware overlay for hairline borders, scrollbars, and
// muted labels that need to sit on a colored surface. Uses the contrast
// color at low opacity so it works on both light and dark backgrounds.
function overlayColor(hex: string, alpha: number): string {
  return isColorDark(hex)
    ? `rgba(255, 255, 255, ${alpha})`
    : `rgba(0, 0, 0, ${alpha})`;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return Math.round(h * 360);
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// ============================================================================
// InfoWindow content builders — one popup for the property pin, one for POIs.
// Built as DOM nodes with textContent (no HTML injection surface) so untrusted
// content-collection strings can't inject markup.
// ============================================================================

// The "Get Directions" / "Visit Website" action pills. Stacked full-width so
// both CTAs share the same width and never clip on tablet/mobile when the
// InfoWindow is narrow. box-sizing keeps the outlined border inside the width.
function actionButtonStyle(fillColor: string, variant: 'primary' | 'secondary'): string {
  const base =
    'font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;' +
    'padding:8px 12px;border-radius:4px;text-decoration:none;display:block;' +
    'width:100%;box-sizing:border-box;text-align:center;outline:none;';
  return variant === 'primary'
    ? `${base}background:${fillColor};color:#fff;border:1px solid ${fillColor};`
    : `${base}border:1px solid ${fillColor};color:#111;background:#fff;`;
}

// Google Maps stamps an inline height/max-height on the InfoWindow scroll
// pane after open, which clips the last stacked CTA. Clear those constraints
// once the DOM is ready (and again on the next frame — Google sometimes
// re-applies the clamp right after domready) so the popup fits its content.
function unlockInfoWindowHeight() {
  const clear = () => {
    const root = document.querySelector('.gm-style-iw');
    if (!root) return;
    const targets = [
      root,
      ...Array.from(root.querySelectorAll<HTMLElement>('.gm-style-iw-c, .gm-style-iw-d')),
    ] as HTMLElement[];
    for (const el of targets) {
      el.style.removeProperty('height');
      el.style.removeProperty('max-height');
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('overflow', 'visible', 'important');
    }
  };
  clear();
  requestAnimationFrame(clear);
  // One more pass after layout settles — Google's height clamp can land late.
  setTimeout(clear, 50);
}

function buildPropertyInfoContent(name: string, address: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'nb-iw-content';
  wrap.style.cssText = 'font-family:var(--vibe-font-body,sans-serif);min-width:180px;max-width:260px;padding:2px 0 4px;box-sizing:border-box;text-align:center;';

  const nameEl = document.createElement('div');
  nameEl.textContent = name || 'Property';
  nameEl.style.cssText = 'font-weight:600;font-size:14px;color:#111;line-height:1.3;text-align:center;';
  wrap.appendChild(nameEl);

  const cleanAddress = stripCountry(address);
  if (cleanAddress) {
    appendAddressBlock(wrap, cleanAddress);
  }

  return wrap;
}

function buildPoiInfoContent(poi: POI, accentColor: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'nb-iw-content';
  // Extra bottom padding gives the outlined Visit Website CTA room so its
  // border isn't flush against (or clipped by) the InfoWindow chrome.
  wrap.style.cssText = 'font-family:var(--vibe-font-body,sans-serif);min-width:180px;max-width:260px;width:100%;padding:2px 0 12px;box-sizing:border-box;text-align:center;';

  const name = document.createElement('div');
  name.textContent = poi.poi_name || 'Point of interest';
  name.style.cssText = 'font-weight:600;font-size:14px;color:#111;line-height:1.3;padding-right:24px;padding-left:24px;text-align:center;';
  wrap.appendChild(name);

  if (hasVal(poi.poi_address)) {
    appendAddressBlock(wrap, poi.poi_address, 'padding-right:8px;padding-left:8px;');
  }

  const dir = hasVal(poi.poi_directions_map_url) ? poi.poi_directions_map_url : '';
  const web = hasVal(poi.poi_business_url) ? poi.poi_business_url : '';
  if (dir || web) {
    // Column stack + 100% width buttons keeps both CTAs equal width and
    // fully visible inside the InfoWindow on tablet/mobile breakpoints.
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-direction:column;gap:6px;margin-top:10px;width:100%;align-items:stretch;';
    if (dir) {
      const a = document.createElement('a');
      a.href = dir;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = 'Get Directions';
      a.style.cssText = actionButtonStyle(accentColor, 'primary');
      actions.appendChild(a);
    }
    if (web) {
      const a = document.createElement('a');
      a.href = web;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = 'Visit Website';
      a.style.cssText = actionButtonStyle(accentColor, 'secondary');
      actions.appendChild(a);
    }
    wrap.appendChild(actions);
  }

  return wrap;
}

// ============================================================================
// Google Map component
// ============================================================================

function GoogleMap({
  propertyLat, propertyLng, propertyName, propertyAddress,
  primaryColor, pois,
  selectedPoiIndex, onMarkerClick, onPropertyClick, focusPois,
}: {
  propertyLat: number;
  propertyLng: number;
  propertyName: string;
  propertyAddress: string;
  primaryColor: string;
  pois: POI[];
  selectedPoiIndex: number | null;
  onMarkerClick: (index: number) => void;
  onPropertyClick: () => void;
  focusPois: POI[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; normalIcon: any; highlightIcon: any; poiIndex: number }[]>([]);
  // Two separate InfoWindow instances so the property popup and POI popup can
  // be shown/hidden independently — but we always close one before opening the
  // other so only a single popup is visible at a time.
  const poiInfoWindowRef = useRef<any>(null);
  const propertyInfoWindowRef = useRef<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !containerRef.current) return;
      const g = (window as any).google.maps;
      const map = new g.Map(containerRef.current, {
        center: { lat: propertyLat, lng: propertyLng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
        styles: buildMapStyles(primaryColor),
      });

      // maxWidth keeps the popup from exceeding the map pane on tablet while
      // still fitting two stacked CTAs without clipping.
      poiInfoWindowRef.current = new g.InfoWindow({ maxWidth: 280 });
      propertyInfoWindowRef.current = new g.InfoWindow({ maxWidth: 280 });
      // Clear Google's post-open height clamp so stacked CTAs aren't clipped.
      g.event.addListener(poiInfoWindowRef.current, 'domready', unlockInfoWindowHeight);
      g.event.addListener(propertyInfoWindowRef.current, 'domready', unlockInfoWindowHeight);

      // Solid surface-orange pin — no property logo or house glyph overlay.
      const propMarker = new g.Marker({
        position: { lat: propertyLat, lng: propertyLng },
        map, title: propertyName || 'Property', zIndex: 999,
        icon: {
          url: makePropertyPinBg(),
          scaledSize: new g.Size(60, 74),
          anchor: new g.Point(30, 74),
        },
      });
      // Clicking the property pin opens its own popup and clears any selected
      // POI. Clearing the selection lets the POI-sync effect close the POI
      // popup, so we never show two InfoWindows at once.
      propMarker.addListener('click', () => {
        poiInfoWindowRef.current?.close();
        onPropertyClick();
        propertyInfoWindowRef.current.setContent(
          buildPropertyInfoContent(propertyName, propertyAddress),
        );
        propertyInfoWindowRef.current.open(map, propMarker);
      });
      mapRef.current = map;
      setReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyLat, propertyLng, primaryColor, propertyName, propertyAddress, onPropertyClick]);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const iconCache = new Map<string, { normal: any; highlight: any }>();

    for (let origIndex = 0; origIndex < pois.length; origIndex++) {
      const poi = pois[origIndex];
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };

      const cat = poi.poi_type || 'Nearby';
      if (!iconCache.has(cat)) {
        const glyph = getCategoryGlyph(cat);
        iconCache.set(cat, {
          normal: { url: makeSvgMarker('#222', glyph, 28), scaledSize: new g.Size(28, 36), anchor: new g.Point(14, 36) },
          highlight: { url: makeHighlightMarker(primaryColor, glyph, 40), scaledSize: new g.Size(40, 54), anchor: new g.Point(20, 54) },
        });
      }

      const icons = iconCache.get(cat)!;
      const isSelected = selectedPoiIndex === origIndex;

      const marker = new g.Marker({
        position: pos, map,
        title: poi.poi_name || '',
        icon: isSelected ? icons.highlight : icons.normal,
        zIndex: isSelected ? 900 : 1,
        animation: isSelected ? g.Animation.BOUNCE : null,
      });

      if (isSelected) setTimeout(() => marker.setAnimation(null), 1400);

      marker.addListener('click', () => onMarkerClick(origIndex));

      markersRef.current.push({ marker, normalIcon: icons.normal, highlightIcon: icons.highlight, poiIndex: origIndex });
    }
  }, [pois, primaryColor, selectedPoiIndex, onMarkerClick]);

  useEffect(() => { if (ready) syncMarkers(); }, [ready, syncMarkers]);

  // Fit bounds to the current focus set (property + focusPois). Runs on mount
  // and whenever the caller changes the focus (e.g. expanded category).
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map || !ready) return;

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;
    for (const poi of focusPois) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;
      bounds.extend({ lat, lng });
      hasPoiBounds = true;
    }
    if (!hasPoiBounds) return;

    map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    const listener = g.event.addListener(map, 'idle', () => {
      if (map.getZoom() > 15) map.setZoom(15);
      g.event.removeListener(listener);
    });
    return () => { g.event.removeListener(listener); };
  }, [focusPois, propertyLat, propertyLng, ready]);

  // When selected POI changes: update marker icons, pan to it, and open the
  // POI popup (closing the property popup if it was open). When the selection
  // clears, close the POI popup — but leave the property popup alone so the
  // property-pin click flow can control it independently.
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    const iw = poiInfoWindowRef.current;
    if (!g || !map || !ready) return;

    let selectedMarker: any = null;
    for (const entry of markersRef.current) {
      const isSelected = entry.poiIndex === selectedPoiIndex;
      entry.marker.setIcon(isSelected ? entry.highlightIcon : entry.normalIcon);
      entry.marker.setZIndex(isSelected ? 900 : 1);
      if (isSelected) selectedMarker = entry.marker;
    }

    if (selectedMarker && selectedPoiIndex !== null) {
      propertyInfoWindowRef.current?.close();
      selectedMarker.setAnimation(g.Animation.BOUNCE);
      setTimeout(() => selectedMarker.setAnimation(null), 1400);
      map.panTo(selectedMarker.getPosition());
      const poi = pois[selectedPoiIndex];
      if (iw && poi) {
        iw.setContent(buildPoiInfoContent(poi, AION_PROPERTY_PIN_COLOR));
        iw.open(map, selectedMarker);
      }
    } else if (iw) {
      iw.close();
    }
  }, [selectedPoiIndex, ready, pois]);

  return (
    <div className="w-full h-full relative overflow-hidden">
      <div ref={containerRef} className="w-full h-full" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5]">
          <span className="font-body text-sm text-[var(--vibe-color-text-muted)] animate-pulse">Loading map…</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

// Aion override: both POI hover and the active/selected item use a fixed brand
// orange (#e2903d) instead of the property secondary color, so the selected
// state reads as a warm accent on top of the primary-color panel — matching
// Aion's dark navy → orange → white treatment used elsewhere on the site.
// Text is forced to white for strong, on-brand contrast against the orange
// fill. Hover and active share the same visual so the selected item stays
// visually consistent with the hover affordance the user just used.
const AION_POI_ACTIVE_BG = '#e2903d';
const AION_POI_ACTIVE_TEXT = '#ffffff';

export function NeighborhoodMap() {
  const property = useProperty();
  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', { collection: 'neighborhood_pois' });
  const heading = useContentToken('neighborhoodmap.heading') as string || 'Explore The Neighborhood';

  const propLat = usePropertyField('property_address_latitude');
  const propLng = usePropertyField('property_address_longitude');
  const propertyName = (property?.property_name as string) || '';
  const propertyAddress = (property?.property_full_address as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#1f3b8b';
  // Pick pure black or white against the primary panel surface so text stays
  // readable regardless of what the brand primary color happens to be. Active
  // POI text is fixed to white because the active background is the fixed
  // Aion brand orange (AION_POI_ACTIVE_BG).
  const panelTextColor = contrastText(primaryColor);
  const panelMutedText = overlayColor(primaryColor, 0.75);
  const panelBorder = overlayColor(primaryColor, 0.25);
  const panelScrollThumb = overlayColor(primaryColor, 0.3);

  const allPois = pois && pois.length > 0 ? pois : [];

  const grouped = useMemo(() => {
    const map = new Map<string, { poi: POI; origIndex: number }[]>();
    allPois.forEach((poi, origIndex) => {
      const cat = poi.poi_type || 'Nearby';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push({ poi, origIndex });
    });
    return Array.from(map.entries());
  }, [allPois]);

  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // POIs the map should fit its viewport to. When a category is expanded,
  // zoom to that subset so all of its markers stay visible; otherwise fit
  // everything.
  const focusPois = useMemo(() => {
    if (expandedCat) {
      const found = grouped.find(([cat]) => cat === expandedCat);
      if (found && found[1].length > 0) return found[1].map((entry) => entry.poi);
    }
    return allPois;
  }, [grouped, expandedCat, allPois]);

  // Initialize/refresh expanded category once POIs load
  useEffect(() => {
    if (expandedCat === null && grouped.length > 0) {
      setExpandedCat(grouped[0][0]);
    }
  }, [grouped, expandedCat]);

  const handleToggleCat = useCallback((cat: string) => {
    setExpandedCat((prev) => (prev === cat ? null : cat));
  }, []);

  const handlePoiClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex((prev) => (prev === origIndex ? null : origIndex));
  }, []);

  const handleMarkerClick = useCallback((origIndex: number) => {
    const poi = allPois[origIndex];
    if (!poi) return;
    const cat = poi.poi_type || 'Nearby';
    setExpandedCat(cat);
    setSelectedPoiIndex(origIndex);
    // Scroll the item into view once the accordion has opened
    setTimeout(() => {
      const el = listRef.current?.querySelector(`[data-poi-index="${origIndex}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
  }, [allPois]);

  // Clicking the property pin clears any active POI selection — the POI-sync
  // effect will then close the POI popup, so we never end up with two popups
  // visible at once. The property pin's own click handler opens its popup.
  const handlePropertyClick = useCallback(() => {
    setSelectedPoiIndex(null);
  }, []);

  const lat = parseFloat(propLat as string || '');
  const lng = parseFloat(propLng as string || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <section className="nb-section">
      <div className="nb-split">
        <div className="nb-map-side">
          {hasCoords ? (
            <GoogleMap
              propertyLat={lat} propertyLng={lng}
              propertyName={propertyName} propertyAddress={propertyAddress}
              primaryColor={primaryColor}
              pois={allPois}
              focusPois={focusPois}
              selectedPoiIndex={selectedPoiIndex}
              onMarkerClick={handleMarkerClick}
              onPropertyClick={handlePropertyClick}
            />
          ) : (
            <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center text-[var(--vibe-color-text-muted)]">
              <p className="font-body">Map coordinates not available</p>
            </div>
          )}
        </div>

        <div
          className="nb-panel"
          style={{
            background: primaryColor,
            color: panelTextColor,
            ['--nb-primary' as any]: primaryColor,
            ['--nb-panel-text' as any]: panelTextColor,
            ['--nb-panel-muted' as any]: panelMutedText,
            ['--nb-panel-border' as any]: panelBorder,
            ['--nb-panel-scroll' as any]: panelScrollThumb,
            ['--nb-poi-active-bg' as any]: AION_POI_ACTIVE_BG,
            ['--nb-poi-active-text' as any]: AION_POI_ACTIVE_TEXT,
          }}
        >
          <div className="nb-panel-inner">
            <EditableToken
              path="neighborhoodmap.heading"
              as="h2"
              className="nb-panel-heading"
            >
              {heading}
            </EditableToken>

            <div ref={listRef} className="nb-cat-list">
              {grouped.length > 0 ? (
                grouped.map(([cat, items]) => {
                  const isOpen = expandedCat === cat;
                  return (
                    <div key={cat} className={`nb-cat${isOpen ? ' nb-cat--open' : ''}`}>
                      <button
                        type="button"
                        className="nb-cat-header"
                        onClick={() => handleToggleCat(cat)}
                        aria-expanded={isOpen}
                      >
                        <span className="nb-cat-icon">
                          <FontAwesomeIcon icon={getCategoryIcon(cat)} />
                        </span>
                        <span className="nb-cat-label">{cat}</span>
                        <span className="nb-cat-toggle" aria-hidden="true">
                          <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
                        </span>
                      </button>
                      {isOpen && (
                        <ul className="nb-cat-items">
                          {items.map(({ poi, origIndex }) => {
                            const isActive = selectedPoiIndex === origIndex;
                            return (
                              <li
                                key={origIndex}
                                data-poi-index={origIndex}
                                className={`nb-cat-item${isActive ? ' nb-cat-item--active' : ''}`}
                                onClick={() => handlePoiClick(origIndex)}
                              >
                                {poi.poi_name || 'Point of interest'}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="nb-empty">No points of interest available.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .nb-section {
          width: 100%;
          background: var(--vibe-color-background, #fff);
          border-bottom: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.15));
          /* Half-inch gap between map and footer */
          padding-bottom: 0.5in;
        }
        .nb-split {
          display: grid;
          grid-template-columns: 1fr;
          min-height: 420px;
        }
        @media (min-width: 900px) {
          .nb-split {
            grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
            height: 640px;
            min-height: 640px;
          }
        }
        .nb-map-side {
          position: relative;
          min-height: 360px;
          width: 100%;
          overflow: hidden;
        }
        @media (min-width: 900px) {
          .nb-map-side { min-height: 100%; }
        }
        .nb-panel {
          color: var(--nb-panel-text, #fff);
          display: flex;
          align-items: stretch;
          min-height: 360px;
        }
        .nb-panel-inner {
          width: 100%;
          padding: 2.25rem 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          flex: 1;
          min-height: 0;
        }
        @media (min-width: 900px) {
          .nb-panel-inner {
            padding: 3rem 3rem 2.5rem;
          }
        }
        .nb-panel-heading {
          font-family: var(--vibe-font-heading);
          color: var(--nb-panel-text, #fff);
          font-size: clamp(1.75rem, 3.5vw, 2.75rem);
          font-weight: 600;
          line-height: 1.15;
          margin: 0 0 0.5rem;
          letter-spacing: -0.01em;
          text-align: center;
        }
        @media (min-width: 900px) {
          .nb-panel-heading { text-align: left; }
        }
        .nb-cat-list {
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: var(--nb-panel-scroll, rgba(255,255,255,0.3)) transparent;
          flex: 1 1 0;
          min-height: 0;
          max-height: 60vh;
        }
        .nb-cat-list::-webkit-scrollbar { width: 4px; }
        .nb-cat-list::-webkit-scrollbar-track { background: transparent; }
        .nb-cat-list::-webkit-scrollbar-thumb { background: var(--nb-panel-scroll, rgba(255,255,255,0.25)); border-radius: 2px; }
        .nb-cat {
          border-bottom: 1px solid var(--nb-panel-border, rgba(255, 255, 255, 0.25));
        }
        .nb-cat:last-child { border-bottom: none; }
        .nb-cat-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 1rem 0;
          background: transparent;
          border: none;
          color: var(--nb-panel-text, #fff);
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          transition: opacity 0.15s ease;
        }
        .nb-cat-header:hover { opacity: 0.85; }
        .nb-cat-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          font-size: 1.25rem;
          color: var(--nb-panel-text, #fff);
          flex-shrink: 0;
        }
        .nb-cat-label {
          flex: 1;
          min-width: 0;
        }
        .nb-cat-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.25rem;
          font-size: 0.9rem;
          color: var(--nb-panel-text, #fff);
          opacity: 0.85;
          flex-shrink: 0;
        }
        .nb-cat-items {
          list-style: none;
          padding: 0 0 0.75rem 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          animation: nb-fade-in 0.2s ease;
        }
        .nb-cat-item {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--nb-panel-muted, rgba(255, 255, 255, 0.9));
          cursor: pointer;
          /* left padding = icon width (2rem) + header gap (1rem) so item text
             lines up with the category label above it */
          padding: 0.55rem 0.85rem 0.55rem 3rem;
          border-radius: 4px;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        /* Aion override: both hover and active/selected use the fixed brand
           orange so the selected item stays visually consistent with the hover
           affordance the user just interacted with. */
        .nb-cat-item:hover,
        .nb-cat-item--active {
          background: var(--nb-poi-active-bg, #e2903d);
          color: var(--nb-poi-active-text, #fff);
          font-weight: 600;
        }
        .nb-empty {
          font-family: var(--vibe-font-body);
          color: var(--nb-panel-muted, rgba(255, 255, 255, 0.8));
          font-size: 0.95rem;
        }
        @keyframes nb-fade-in {
          from { opacity: 0; transform: translateY(-2px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nb-cat-items { animation: none; }
        }

        /* ===== Google Maps info window cleanup =====
           Give the property + POI popups a compact, on-brand look on every
           breakpoint. Google injects these classnames on the InfoWindow chrome
           and stamps inline height on the scroll pane — override both so
           stacked CTAs render fully (especially Visit Website at the bottom). */
        .gm-style .gm-style-iw {
          max-height: none !important;
          height: auto !important;
        }
        .gm-style .gm-style-iw-c {
          border-radius: 6px !important;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
          padding: 12px 14px 16px !important;
          max-width: min(280px, calc(100vw - 48px)) !important;
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
          box-sizing: border-box !important;
        }
        .gm-style .gm-style-iw-d {
          overflow: visible !important;
          max-height: none !important;
          height: auto !important;
        }
        .gm-style .gm-style-iw-tc::after { background: #fff !important; }
        .nb-iw-content { overflow: visible; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {
    'neighborhoodmap.heading': {
      label: 'Panel Heading',
      type: 'text',
      defaultValue: 'Explore The Neighborhood',
    },
  },
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as any).contentSchema = contentSchema;

export default NeighborhoodMap;
